import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ChevronRight, ChevronLeft, Plus, Trash2, BookOpen, CheckCircle, GripVertical } from '../../components/CustomIcons';

const CATEGORIES = ['Technology', 'Business', 'Healthcare', 'Logistics', 'Academic', 'Skilled Trades', 'Other'];
const STEPS = ['Course Details', 'Curriculum', 'Review & Publish'];

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '32px' }}>
      {STEPS.map((step, i) => (
        <React.Fragment key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: i < current ? 'var(--success)' : i === current ? 'var(--orange)' : 'var(--bg-elevated)', border: `2px solid ${i < current ? 'var(--success)' : i === current ? 'var(--orange)' : 'var(--border-default)'}`, color: i <= current ? (i === current ? '#111111' : '#FFFFFF') : 'var(--text-muted)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', flexShrink: 0, transition: 'all 0.2s ease' }}>
              {i < current ? <CheckCircle size={15} /> : i + 1}
            </div>
            <span style={{ fontSize: '13px', fontWeight: i === current ? 600 : 400, color: i === current ? 'var(--text-primary)' : 'var(--text-muted)', fontFamily: 'Plus Jakarta Sans, sans-serif', whiteSpace: 'nowrap' }}>
              {step}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex: 1, height: '2px', background: i < current ? 'var(--success)' : 'var(--border-default)', margin: '0 12px', transition: 'background 0.3s ease' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ModuleRow({ module, index, onChange, onRemove, onDragStart, onDragOver, onDrop, isDragging }) {
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, index)}
      onDragOver={e => { e.preventDefault(); onDragOver(e, index); }}
      onDrop={e => { e.preventDefault(); onDrop(e, index); }}
      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: isDragging ? 'rgba(249,115,22,0.08)' : 'var(--bg-elevated)', borderRadius: '10px', border: isDragging ? '1px solid rgba(249,115,22,0.4)' : '1px solid var(--border-subtle)', marginBottom: '10px', cursor: 'grab', transition: 'all 0.15s ease' }}>
      <GripVertical size={16} style={{ color: 'var(--text-muted)', cursor: 'grab', flexShrink: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '26px', height: '26px', borderRadius: '6px', background: 'var(--orange-muted)', color: 'var(--orange)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>
        {index + 1}
      </div>
      <input value={module.title} onChange={e => onChange(index, 'title', e.target.value)}
        placeholder={`Module ${index + 1} title`}
        style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif', fontSize: '14px' }} />
      <input type="number" value={module.duration_hours} onChange={e => onChange(index, 'duration_hours', parseInt(e.target.value) || 0)}
        placeholder="Hours"
        style={{ width: '70px', background: 'var(--bg-input)', border: '1px solid var(--border-default)', borderRadius: '7px', color: 'var(--text-secondary)', fontSize: '13px', padding: '6px 10px', outline: 'none' }} />
      <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>hr</span>
      <button onClick={() => onRemove(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '4px', borderRadius: '6px' }}
        onMouseEnter={e => e.currentTarget.style.color = 'var(--error)'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>
        <Trash2 size={15} />
      </button>
    </div>
  );
}

export default function CourseBuilder() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const [form, setForm] = useState({
    title: '',
    category: 'Technology',
    description: '',
    duration_hours: 40,
    price_credits: 0,
    tags: '',
    modules: [
      { id: '1', title: '', duration_hours: 10, order: 1 },
    ],
  });

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const updateModule = (index, key, value) => {
    setForm(prev => {
      const modules = [...prev.modules];
      modules[index] = { ...modules[index], [key]: value };
      return { ...prev, modules };
    });
  };

  const addModule = () => {
    setForm(prev => ({
      ...prev,
      modules: [...prev.modules, { id: String(Date.now()), title: '', duration_hours: 10, order: prev.modules.length + 1 }]
    }));
  };

  const removeModule = (index) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.filter((_, i) => i !== index).map((m, i) => ({ ...m, order: i + 1 }))
    }));
  };

  const handleDragStart = (e, index) => {
    setDragFrom(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOver(index);
  };

  const handleDrop = (e, toIndex) => {
    e.preventDefault();
    if (dragFrom === null || dragFrom === toIndex) { setDragFrom(null); setDragOver(null); return; }
    setForm(prev => {
      const modules = [...prev.modules];
      const [moved] = modules.splice(dragFrom, 1);
      modules.splice(toIndex, 0, moved);
      return { ...prev, modules: modules.map((m, i) => ({ ...m, order: i + 1 })) };
    });
    setDragFrom(null);
    setDragOver(null);
  };

  const validateStep = () => {
    const errs = {};
    if (step === 0) {
      if (!form.title.trim()) errs.title = 'Title is required';
      if (!form.description.trim()) errs.description = 'Description is required';
      if (!form.duration_hours || form.duration_hours < 1) errs.duration_hours = 'Enter valid hours';
    }
    if (step === 1) {
      if (form.modules.some(m => !m.title.trim())) errs.modules = 'All modules need a title';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => { if (validateStep()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const totalHours = form.modules.reduce((a, m) => a + (m.duration_hours || 0), 0);

  const publish = async (status) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        category: form.category,
        description: form.description,
        duration_hours: form.duration_hours,
        price_credits: form.price_credits,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        modules: form.modules.map((m, i) => ({ ...m, order: i + 1 })),
      };
      const res = await authFetch('/marketplace/provider/courses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (status === 'published' && res.data?.id) {
        await authFetch(`/marketplace/provider/courses/${res.data.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'published' })
        });
      }
      navigate('/marketplace/provider/courses');
    } catch (e) { console.error('course-save error:', e); } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="course-builder">
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>

        <div style={{ marginBottom: '24px' }}>
          <div className="overline" style={{ marginBottom: '4px' }}>Provider Portal</div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>
            Course Builder
          </h1>
        </div>

        <StepIndicator current={step} />

        {/* ── Step 0: Course Details ── */}
        {step === 0 && (
          <div className="card" style={{ padding: '28px' }}>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)', margin: '0 0 20px 0' }}>Course Details</h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>Course Title *</label>
              <input className="input-dark" value={form.title} onChange={e => set('title', e.target.value)}
                placeholder="e.g. CompTIA A+ Certification Prep" data-testid="course-title" />
              {errors.title && <p style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>{errors.title}</p>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>Category *</label>
                <select className="input-dark" value={form.category} onChange={e => set('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>Total Hours *</label>
                <input type="number" className="input-dark" value={form.duration_hours} onChange={e => set('duration_hours', parseInt(e.target.value) || 0)}
                  placeholder="40" data-testid="course-hours" />
                {errors.duration_hours && <p style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>{errors.duration_hours}</p>}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>Description *</label>
              <textarea className="input-dark" value={form.description} onChange={e => set('description', e.target.value)}
                placeholder="Describe what learners will gain from this course. Be specific about outcomes and certifications..." rows={5} data-testid="course-description"
                style={{ resize: 'vertical', lineHeight: 1.6 }} />
              {errors.description && <p style={{ color: 'var(--error)', fontSize: '12px', marginTop: '4px' }}>{errors.description}</p>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>Price (Credits) — 0 = Free</label>
                <input type="number" className="input-dark" value={form.price_credits} onChange={e => set('price_credits', parseInt(e.target.value) || 0)}
                  placeholder="0" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '6px' }}>Tags (comma-separated)</label>
                <input className="input-dark" value={form.tags} onChange={e => set('tags', e.target.value)}
                  placeholder="certification, hands-on, beginner" />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 1: Curriculum ── */}
        {step === 1 && (
          <div className="card" style={{ padding: '28px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)', margin: 0 }}>Curriculum Modules</h2>
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{form.modules.length} modules · {totalHours}h total</span>
            </div>

            {errors.modules && (
              <div style={{ background: 'var(--error-bg)', border: '1px solid var(--error)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', color: 'var(--error)', fontSize: '13px' }}>
                {errors.modules}
              </div>
            )}

            {form.modules.map((m, i) => (
              <ModuleRow key={m.id} module={m} index={i} onChange={updateModule} onRemove={removeModule}
                onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop}
                isDragging={dragFrom === i || dragOver === i} />
            ))}

            <button onClick={addModule} className="btn-secondary" style={{ width: '100%', justifyContent: 'center', marginTop: '4px' }}
              data-testid="add-module-btn">
              <Plus size={15} /> Add Module
            </button>
          </div>
        )}

        {/* ── Step 2: Review ── */}
        {step === 2 && (
          <div className="card" style={{ padding: '28px' }}>
            <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: 'var(--text-primary)', margin: '0 0 20px 0' }}>Review & Publish</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {[
                { label: 'Title', value: form.title },
                { label: 'Category', value: form.category },
                { label: 'Duration', value: `${form.duration_hours} hours` },
                { label: 'Price', value: form.price_credits === 0 ? 'Free' : `${form.price_credits.toLocaleString()} credits` },
                { label: 'Modules', value: `${form.modules.length} modules (${totalHours}h total)` },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--bg-elevated)', borderRadius: '8px' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif' }}>{label}</span>
                  <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>

            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: 1.6 }}>
              {form.description}
            </p>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={() => publish('draft')} disabled={saving} style={{ flex: 1, justifyContent: 'center' }}
                data-testid="save-draft-btn">
                Save as Draft
              </button>
              <button className="btn-primary" onClick={() => publish('published')} disabled={saving} style={{ flex: 1, justifyContent: 'center' }}
                data-testid="publish-course-btn">
                {saving ? 'Publishing...' : 'Publish Course'}
              </button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <button className="btn-secondary" onClick={back} disabled={step === 0} style={{ opacity: step === 0 ? 0.4 : 1 }}>
            <ChevronLeft size={16} /> Back
          </button>
          {step < STEPS.length - 1 && (
            <button className="btn-primary" onClick={next} data-testid="next-step-btn">
              Next <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
